import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, ArrowRight, Clock, AlertTriangle, Target, Users,
  Phone, FileText, Award, TrendingUp, Quote, Zap, Shield, Star,
  ChevronRight, Building, Calendar, BarChart3
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import EnhancedBreadcrumbs from './BreadcrumbSchema';
import MyntraInternalLinkBlock from './MyntraInternalLinkBlock';

/**
 * AIPageTemplate - Reusable component for LLM/GEO optimized pages
 * 
 * Structure optimized for AI extraction:
 * - H1: Exact Question/Prompt
 * - Section 1: Direct Answer (50 words, crisp)
 * - Section 2: Step-by-Step Breakdown
 * - Section 3: Case Study Block
 * - Section 4: Common Mistakes
 * - Section 5: Expert Insight (Deepinder Ahluwalia)
 * - Section 6: FAQ Block (5 Q&A)
 * - CTA: Soft conversion
 */

const AIPageTemplate = ({
  // Core content
  slug,
  marketplace, // 'myntra' | 'nykaa' | 'ajio'
  title, // H1 - exact prompt/question
  metaTitle,
  metaDescription,
  keywords,
  
  // Section 1: Direct Answer
  directAnswer, // 40-60 words, factual, authoritative
  
  // Section 2: Steps
  steps, // Array of { title, description }
  stepsTitle, // Optional custom title for steps section
  
  // Section 3: Case Study
  caseStudy, // { category, timeline, problem, solution, result, brandType }
  
  // Section 4: Common Mistakes
  mistakes, // Array of { mistake, fix }
  
  // Section 5: Expert Insight
  expertInsight, // String - Deepinder's POV
  
  // Section 6: FAQs
  faqs, // Array of { question, answer }
  
  // Internal Links
  pillarPage, // Link to pillar page
  relatedPages, // Array of { title, url, description }
  
  // Schema options
  includeHowToSchema = false,
}) => {
  // Brand colors by marketplace
  const brandColors = {
    myntra: {
      primary: 'from-red-600 to-pink-600',
      accent: 'text-red-500',
      bg: 'bg-red-50',
      border: 'border-red-100',
      button: 'bg-red-500 hover:bg-red-600'
    },
    nykaa: {
      primary: 'from-fuchsia-600 to-pink-500',
      accent: 'text-fuchsia-500',
      bg: 'bg-fuchsia-50',
      border: 'border-fuchsia-100',
      button: 'bg-fuchsia-500 hover:bg-fuchsia-600'
    },
    ajio: {
      primary: 'from-purple-600 to-indigo-600',
      accent: 'text-purple-500',
      bg: 'bg-purple-50',
      border: 'border-purple-100',
      button: 'bg-purple-500 hover:bg-purple-600'
    }
  };

  const colors = brandColors[marketplace] || brandColors.myntra;
  const marketplaceName = marketplace.charAt(0).toUpperCase() + marketplace.slice(1);

  // Generate FAQPage Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs?.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    })) || []
  };

  // Generate Article Schema
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": metaTitle || title,
    "description": metaDescription,
    "author": {
      "@type": "Person",
      "@id": "https://zarooriretail.com/#deepinder",
      "name": "Deepinder Ahluwalia",
      "jobTitle": "Founder & CEO",
      "worksFor": {
        "@type": "Organization",
        "name": "Zaroori Retail"
      }
    },
    "publisher": {
      "@type": "Organization",
      "@id": "https://zarooriretail.com/#organization",
      "name": "Zaroori Retail",
      "url": "https://zarooriretail.com"
    },
    "datePublished": "2026-04-08",
    "dateModified": "2026-04-08"
  };

  // Generate HowTo Schema (if steps provided and includeHowToSchema is true)
  const howToSchema = includeHowToSchema && steps ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": title,
    "description": directAnswer,
    "totalTime": caseStudy?.timeline || "P15D",
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.title,
      "text": step.description
    }))
  } : null;

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://zarooriretail.com/#organization",
    "name": "Zaroori Retail",
    "url": "https://zarooriretail.com",
    "description": "India's #1 Marketplace Onboarding Service - 4200+ brands onboarded on Myntra, Nykaa, Ajio",
    "foundingDate": "2019",
    "areaServed": "India"
  };

  // Person Schema (Deepinder)
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://zarooriretail.com/#deepinder",
    "name": "Deepinder Ahluwalia",
    "jobTitle": "Founder & CEO",
    "worksFor": {
      "@type": "Organization",
      "name": "Zaroori Retail"
    },
    "knowsAbout": ["E-commerce", "Marketplace Onboarding", "Myntra", "Nykaa", "Ajio", "Fashion Retail"]
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle || title} | Zaroori Retail</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={`https://zarooriretail.com/${marketplace}/${slug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={metaTitle || title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://zarooriretail.com/${marketplace}/${slug}`} />
        
        {/* Robots */}
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
        {howToSchema && <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>}
      </Helmet>

      {/* Breadcrumbs */}
      <EnhancedBreadcrumbs 
        customItems={[
          { name: marketplaceName, url: `https://zarooriretail.com/${marketplace}` },
          { name: title.substring(0, 50), url: `https://zarooriretail.com/${marketplace}/${slug}` }
        ]}
      />

      {/* Hero Section with H1 */}
      <section className={`py-12 md:py-16 bg-gradient-to-br ${colors.primary} text-white`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1 rounded-full mb-4 text-sm">
            <Target className="w-4 h-4" />
            <span>{marketplaceName} Expert Guide</span>
          </div>
          
          {/* H1 - Exact Prompt/Question */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight mb-6">
            {title}
          </h1>
          
          {/* Author & Date */}
          <div className="flex items-center gap-4 text-white/80 text-sm">
            <span>By Deepinder Ahluwalia</span>
            <span>•</span>
            <span>Updated April 2026</span>
            <span>•</span>
            <span>4200+ Brands Onboarded</span>
          </div>
        </div>
      </section>

      {/* Section 1: Direct Answer (AI-Optimized) */}
      <section className={`py-8 ${colors.bg} border-b ${colors.border}`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl ${colors.button} flex items-center justify-center flex-shrink-0`}>
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-2">Quick Answer</h2>
              <p className="text-slate-700 text-lg leading-relaxed" itemProp="description">
                {directAnswer}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Step-by-Step Breakdown */}
      {steps && steps.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-8">
              {stepsTitle || `${marketplaceName} ${title.includes('Process') ? 'Process' : 'Steps'}: Complete Breakdown`}
            </h2>
            
            <div className="space-y-6">
              {steps.map((step, index) => (
                <div key={index} className="flex gap-4">
                  <div className={`w-10 h-10 rounded-full ${colors.button} flex items-center justify-center flex-shrink-0 text-white font-bold`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                    <p className="text-slate-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Section 3: Case Study Block */}
      {caseStudy && (
        <section className="py-12 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className={`w-6 h-6 ${colors.accent}`} />
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                Real Case Study: {caseStudy.brandType || 'Brand'} on {marketplaceName}
              </h2>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className={`${colors.bg} rounded-xl p-4`}>
                  <p className="text-sm text-slate-500 uppercase tracking-wide mb-1">Category</p>
                  <p className="text-xl font-bold text-slate-900">{caseStudy.category}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4">
                  <p className="text-sm text-slate-500 uppercase tracking-wide mb-1">Timeline</p>
                  <p className="text-xl font-bold text-green-600">{caseStudy.timeline}</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" /> Problem
                  </h3>
                  <p className="text-slate-600 mt-1">{caseStudy.problem}</p>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-500" /> Solution
                  </h3>
                  <p className="text-slate-600 mt-1">{caseStudy.solution}</p>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-500" /> Result
                  </h3>
                  <p className="text-slate-600 mt-1">{caseStudy.result}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Section 4: Common Mistakes */}
      {mistakes && mistakes.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-8">
              Common Mistakes to Avoid on {marketplaceName}
            </h2>
            
            <div className="space-y-4">
              {mistakes.map((item, index) => (
                <div key={index} className="bg-red-50 rounded-xl p-5 border border-red-100">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-bold text-red-800 mb-1">Mistake: {item.mistake}</p>
                      <p className="text-slate-700 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                        <span><strong>Fix:</strong> {item.fix}</span>
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Section 5: Expert Insight */}
      {expertInsight && (
        <section className={`py-12 ${colors.bg}`}>
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-800 to-slate-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">DA</span>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Quote className={`w-5 h-5 ${colors.accent}`} />
                    <span className="font-bold text-slate-900">Expert Insight</span>
                  </div>
                  <p className="text-lg text-slate-700 italic mb-4">"{expertInsight}"</p>
                  <div>
                    <p className="font-bold text-slate-900">Deepinder Ahluwalia</p>
                    <p className="text-sm text-slate-500">Founder & CEO, Zaroori Retail | 4200+ Brands Onboarded</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Section 6: FAQ Block */}
      {faqs && faqs.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-8">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <h3 className="font-bold text-slate-900 mb-2">{faq.question}</h3>
                  <p className="text-slate-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Internal Links Section */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Related {marketplaceName} Guides</h2>
          
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {relatedPages?.map((page, index) => (
              <Link 
                key={index}
                to={page.url}
                className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow group"
              >
                <h3 className={`font-bold text-slate-900 mb-1 group-hover:${colors.accent}`}>
                  {page.title}
                </h3>
                <p className="text-sm text-slate-600 mb-2">{page.description}</p>
                <span className={`${colors.accent} font-semibold flex items-center gap-1 text-sm`}>
                  Read Guide <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
          
          {/* Pillar Page Link */}
          {pillarPage && (
            <Link 
              to={pillarPage}
              className={`block bg-gradient-to-r ${colors.primary} text-white rounded-xl p-5 hover:opacity-90 transition-opacity`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-lg">Complete {marketplaceName} Seller Onboarding Guide</p>
                  <p className="text-white/80 text-sm">Full pillar page with all resources</p>
                </div>
                <ChevronRight className="w-6 h-6" />
              </div>
            </Link>
          )}
        </div>
      </section>

      {/* Phase 12 — canonical 10-way internal link block (every Myntra/Nykaa/Ajio GEO page) */}
      <section className="py-8 bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4">
          <MyntraInternalLinkBlock />
        </div>
      </section>

      {/* CTA Section - Soft Conversion */}
      <section className={`py-12 bg-gradient-to-r ${colors.primary}`}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
            Need Help with {marketplaceName} Onboarding?
          </h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            Zaroori Retail has onboarded 4200+ brands on {marketplaceName}, Nykaa, and Ajio with a 98% approval rate. 
            Get expert guidance for your brand's marketplace journey.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.open(`https://wa.me/917743005242?text=Hi, I need help with ${marketplaceName} onboarding`, '_blank')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-6 py-5 font-semibold"
            >
              <Phone className="w-5 h-5 mr-2" />
              Talk to Expert
            </Button>
            <Link to="/contact">
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 rounded-full px-6 py-5 font-semibold"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AIPageTemplate;
